// =============================================================================
// src/pages/InviteAcceptPage.tsx
// Landing page for org invite links: /account/org/invite/:token
//
// Flow:
//   1. On mount, call org_invite_lookup_public to render the "you've been
//      invited to {Org} as {role}" preview. Works for anon visitors so the
//      page renders cleanly when clicked from an email before sign-in.
//   2. If invite is expired / revoked / accepted-by-someone-else, show the
//      relevant state and a way out.
//   3. If signed in with the matching email: one-click Accept button calls
//      org_invite_accept and redirects to /account/org.
//   4. If signed out: show "Sign in / Sign up" CTAs that round-trip back
//      here via ?next=<this-url> so the auto-accept fires after auth.
//   5. If signed in with a different email: show "wrong account" message
//      with a sign-out CTA.
// =============================================================================

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Mail, AlertTriangle } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'
import { useAuth } from '@/lib/auth'
import {
  acceptInvite,
  lookupInvitePublic,
  type PublicInviteInfo,
} from '@/lib/org'

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const { user, session, loading: authLoading, signOut } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [invite, setInvite] = useState<PublicInviteInfo | null>(null)
  const [lookupErr, setLookupErr] = useState<string | null>(null)
  const [lookupLoading, setLookupLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [acceptErr, setAcceptErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!token) {
        setLookupErr('Missing invite token.')
        setLookupLoading(false)
        return
      }
      const { invite, error } = await lookupInvitePublic(token)
      if (cancelled) return
      if (error) setLookupErr(error)
      else setInvite(invite)
      setLookupLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  // Auto-accept once signed in with the matching email. Gated on
  // `acceptErr` too — without it, a failed RPC call leaves `accepting`
  // back at false with no other state change, the effect fires again on
  // the next render, and we end up in a tight retry loop hammering the
  // backend until the user navigates away.
  useEffect(() => {
    if (authLoading || lookupLoading) return
    if (!session || !invite) return
    if (invite.accepted_at || invite.revoked_at) return
    if (new Date(invite.expires_at) <= new Date()) return
    if (user?.email && invite.email.toLowerCase() !== user.email.toLowerCase()) return
    if (accepting) return
    if (acceptErr) return
    if (!token) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAccepting(true)
    void (async () => {
      const { error } = await acceptInvite(token)
      if (error) {
        setAcceptErr(error)
        setAccepting(false)
        return
      }
      navigate('/account/org', { replace: true })
    })()
  }, [authLoading, lookupLoading, session, invite, user?.email, accepting, acceptErr, token, navigate])

  return (
    <SiteLayout>
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-8">
          <Header invite={invite} />
          <Body
            loading={lookupLoading || authLoading}
            err={lookupErr ?? acceptErr}
            invite={invite}
            sessionEmail={user?.email ?? null}
            isSignedIn={!!session}
            accepting={accepting}
            nextHref={`${window.location.pathname}${searchParams.size ? `?${searchParams}` : ''}`}
            onSignOut={async () => {
              await signOut()
            }}
          />
        </div>
      </div>
    </SiteLayout>
  )
}

interface HeaderProps { invite: PublicInviteInfo | null }
function Header({ invite }: HeaderProps) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <span
        aria-hidden
        className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
      >
        <Mail size={20} strokeWidth={2.2} />
      </span>
      <div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-strong)]">
          {invite ? `Join ${invite.organization_name}` : 'Organization invite'}
        </h1>
        {invite ? (
          <p className="mt-1 text-[13px] text-[var(--color-muted)]">
            {invite.inviter_first_name ? `${invite.inviter_first_name} invited` : 'Invited'} you to join as <span className="capitalize">{invite.role}</span>.
          </p>
        ) : null}
      </div>
    </div>
  )
}

interface BodyProps {
  loading: boolean
  err: string | null
  invite: PublicInviteInfo | null
  sessionEmail: string | null
  isSignedIn: boolean
  accepting: boolean
  nextHref: string
  onSignOut: () => Promise<void> | void
}
function Body({ loading, err, invite, sessionEmail, isSignedIn, accepting, nextHref, onSignOut }: BodyProps) {
  if (loading) return <p className="text-[13px] text-[var(--color-muted)]">Looking up invite…</p>
  if (err) {
    return (
      <p className="text-[13px] text-[var(--color-signal)]">
        <AlertTriangle size={14} className="inline-block mr-1 -mt-0.5" aria-hidden />
        {err}
      </p>
    )
  }
  if (!invite) {
    return <p className="text-[13px] text-[var(--color-muted)]">Invite not found.</p>
  }
  if (invite.revoked_at) {
    return <p className="text-[13px] text-[var(--color-signal)]">This invite has been revoked.</p>
  }
  if (invite.accepted_at) {
    return (
      <div className="text-[13.5px] text-[var(--color-accent)]">
        <CheckCircle2 size={14} className="inline-block mr-1 -mt-0.5" aria-hidden />
        Invite already accepted.{' '}
        <Link to="/account/org" className="underline underline-offset-2">
          Open the organization
        </Link>
        .
      </div>
    )
  }
  if (new Date(invite.expires_at) <= new Date()) {
    return (
      <p className="text-[13px] text-[var(--color-signal)]">
        This invite has expired. Ask the inviter to send a new one.
      </p>
    )
  }

  if (!isSignedIn) {
    const next = encodeURIComponent(nextHref)
    return (
      <div className="space-y-3">
        <p className="text-[13px] text-[var(--color-muted)]">
          Sign in or create an account with <span className="font-[var(--font-mono)]">{invite.email}</span> to accept this invite.
        </p>
        <div className="flex gap-3">
          <Link
            to={`/sign-in?next=${next}`}
            className="inline-flex items-center justify-center px-4 py-2 rounded-[var(--radius-DEFAULT)] bg-[var(--color-accent)] text-[var(--color-surface)] text-[13px] font-medium hover:bg-[var(--color-accent-hover)] cursor-pointer"
          >
            Sign in
          </Link>
          <Link
            to={`/sign-up?next=${next}`}
            className="inline-flex items-center justify-center px-4 py-2 rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)] text-[13px] hover:border-[var(--color-accent)]/50 cursor-pointer"
          >
            Create account
          </Link>
        </div>
      </div>
    )
  }

  if (sessionEmail && sessionEmail.toLowerCase() !== invite.email.toLowerCase()) {
    return (
      <div className="space-y-3 text-[13px]">
        <p className="text-[var(--color-signal)]">
          <AlertTriangle size={14} className="inline-block mr-1 -mt-0.5" aria-hidden />
          You're signed in as <span className="font-[var(--font-mono)]">{sessionEmail}</span>, but this invite was sent to{' '}
          <span className="font-[var(--font-mono)]">{invite.email}</span>.
        </p>
        <button
          type="button"
          onClick={() => void onSignOut()}
          className="inline-flex items-center justify-center px-4 py-2 rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)] text-[13px] hover:border-[var(--color-accent)]/50 cursor-pointer"
        >
          Sign out
        </button>
      </div>
    )
  }

  // Matching email + signed in: the auto-accept effect handles it; show a
  // working spinner-ish state.
  return (
    <p className="text-[13px] text-[var(--color-muted)]">
      {accepting ? 'Accepting…' : 'Accepting…'}
    </p>
  )
}
